// src/views/Blogs/Modal/BlogModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { modeConstants } from "../constants";
import { blogActions } from "../redux/actions";
import {
  Calendar,
  Link,
  Tag,
  Image as ImageIcon,
  Eye,
  CheckCircle,
  Type,
  FileText,
  Grid,
  Globe,
  XCircle,
  AlertCircle,
  Edit3,
  FilePlus,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Share2,
} from "react-feather";
import JoditEditorComponent from "./SummernoteEditor";

const DEFAULT_VALUES = {
  date: "",
  blog_url: "",
  image: null,
  short_title: "",
  long_title: "",
  tag: "",
  details: "",
  meta_title: "",
  meta_description: "",
  alt: "",
  is_show_on_home_page: false,
  is_active: true,
  facebook: "",
  instagram: "",
  twitter: "",
  linkedin: "",
  schema_markup: "",
};

const InputField = ({
  icon: Icon,
  label,
  error,
  children,
  required = false,
}) => (
  <div className="mb-3">
    <label className="form-label d-flex align-items-center gap-2 fw-semibold text-dark mb-2">
      {Icon && <Icon size={16} className="text-primary" />}
      {label}
      {required && <span className="text-danger ms-1">*</span>}
    </label>
    {children}
    {error && (
      <div className="d-flex align-items-center gap-1 mt-1 text-danger">
        <AlertCircle size={14} />
        <small>{error.message}</small>
      </div>
    )}
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
    {Icon && <Icon size={20} className="text-primary" />}
    <div>
      <h6 className="mb-0 fw-semibold">{title}</h6>
      {subtitle && <small className="text-muted">{subtitle}</small>}
    </div>
  </div>
);

export default function BlogModal({ show, onHide, mode }) {
  const isAdd = mode === modeConstants.ADD_MODE;
  const isEdit = mode === modeConstants.UPDATE_MODE;
  const isView = mode === modeConstants.VIEW_MODE;
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    mode: "onChange",
  });

  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const [longTitleContent, setLongTitleContent] = useState("");
  const [detailsContent, setDetailsContent] = useState("");
  const initialData = useSelector((s) => s.blogReducer.formData);

  useEffect(() => {
    if (!show) return;

    if (isAdd) {
      reset(DEFAULT_VALUES);
      setImageFile(null);
      setPreviewImage("");
      setLongTitleContent("");
      setDetailsContent("");
      return;
    }

    if (initialData && (isEdit || isView)) {
      const formData = {
        date: initialData.date ?? "",
        blog_url: initialData.blog_url ?? "",
        short_title: initialData.short_title ?? "",
        long_title: initialData.long_title ?? "",
        tag: initialData.tag ?? "",
        details: initialData.details ?? "",
        meta_title: initialData.meta_title ?? "",
        meta_description: initialData.meta_description ?? "",
        alt: initialData.alt ?? "",
        is_show_on_home_page: initialData.is_show_on_home_page ?? false,
        is_active: initialData.is_active ?? true,
        facebook: initialData.facebook ?? "",
        instagram: initialData.instagram ?? "",
        twitter: initialData.twitter ?? "",
        linkedin: initialData.linkedin ?? "",
        schema_markup: initialData.schema_markup ?? "",
      };

      reset(formData);
      setLongTitleContent(initialData.long_title ?? "");
      setDetailsContent(initialData.details ?? "");
      setPreviewImage(
        process.env.REACT_APP_STORAGE_URL + initialData.image_url ?? "",
      );
    }
  }, [show, isAdd, isEdit, isView, initialData, reset]);

  const onSubmit = async (values) => {
    const fd = new FormData();
    fd.append("date", values.date || "");
    fd.append("blog_url", values.blog_url || "");
    fd.append("short_title", values.short_title || "");
    fd.append("long_title", values.long_title || "");
    fd.append("tag", values.tag || "");
    fd.append("details", values.details || "");
    fd.append("meta_title", values.meta_title || "");
    fd.append("meta_description", values.meta_description || "");
    fd.append("alt", values.alt || "");
    fd.append("is_show_on_home_page", values.is_show_on_home_page ? 1 : 0);
    fd.append("is_active", values.is_active ? 1 : 0);
    fd.append("facebook", values.facebook || "");
    fd.append("instagram", values.instagram || "");
    fd.append("twitter", values.twitter || "");
    fd.append("linkedin", values.linkedin || "");
    fd.append("schema_markup", values.schema_markup || "");

    if (imageFile) {
      fd.append("image", imageFile);
    }

    if (isEdit && initialData?.id) {
      dispatch(blogActions.updateBlog(fd, initialData.id, onHide));
    } else {
      dispatch(blogActions.createBlog(fd, onHide));
    }
  };

  const title = isAdd ? "Add Blog" : isEdit ? "Edit Blog" : "View Blog";

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <Modal.Title className="fw-bold text-light">{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-0">
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={isView}>
            <div className="row g-4">
              {/* SEO SETTINGS - MOVED TO TOP */}
              <div className="col-12">
                <SectionHeader
                  icon={Globe}
                  title="SEO Settings"
                  subtitle="All SEO fields are required"
                />
              </div>

              {/* META TITLE - Required */}
              <div className="col-md-12">
                <InputField
                  label="Meta Title"
                  error={errors.meta_title}
                  required
                >
                  <input
                    id="meta_title"
                    className={`form-control ${errors.meta_title ? "is-invalid" : ""}`}
                    {...register("meta_title", {
                      required: "Meta title is required",
                    })}
                    placeholder="SEO title"
                  />
                </InputField>
              </div>

              {/* META DESCRIPTION - Required */}
              <div className="col-md-12">
                <InputField
                  label="Meta Description"
                  error={errors.meta_description}
                  required
                >
                  <textarea
                    id="meta_description"
                    className={`form-control ${errors.meta_description ? "is-invalid" : ""}`}
                    style={{ height: "100px", resize: "vertical" }}
                    {...register("meta_description", {
                      required: "Meta description is required",
                    })}
                    placeholder="SEO description"
                  />
                </InputField>
              </div>
              {/* SCHEMA MARKUP */}
              <div className="col-md-12">
                <InputField
                  label="Schema Markup"
                  error={errors.schema_markup}
                  subtitle="Add your SEO schema here (JSON only - NO <script> tags)"
                >
                  <textarea
                    id="schema_markup"
                    className={`form-control ${errors.schema_markup ? "is-invalid" : ""}`}
                    style={{ height: "120px", resize: "vertical", fontFamily: 'monospace' }}
                    {...register("schema_markup", {
                      validate: (value) => {
                        if (value && /<script/i.test(value)) {
                          return "Please add only JSON markup without <script> tags";
                        }
                        return true;
                      }
                    })}
                    placeholder='{ "@context": "https://schema.org", ... }'
                  />
                  <small className="text-info">
                    Note: Do not include &lt;script&gt; tags here. Just the JSON content.
                  </small>
                </InputField>
              </div>

              {/* BASIC SETTINGS */}
              <div className="col-12">
                <SectionHeader
                  icon={FileText}
                  title="Basic Settings"
                  subtitle="All fields are required"
                />
              </div>

              {/* DATE - Required */}
              <div className="col-md-6">
                <InputField
                  icon={Calendar}
                  label="Date"
                  error={errors.date}
                  required
                >
                  <input
                    id="date"
                    type="date"
                    className={`form-control ${errors.date ? "is-invalid" : ""}`}
                    {...register("date", {
                      required: "Date is required",
                    })}
                    placeholder="Select date"
                  />
                </InputField>
              </div>

              {/* BLOG URL - Required */}
              <div className="col-md-6">
                <InputField
                  icon={Link}
                  label="Blog URL / Slug"
                  error={errors.blog_url}
                  required
                >
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      /
                    </span>
                    <input
                      id="blog_url"
                      className={`form-control border-start-0 ${errors.blog_url ? "is-invalid" : ""}`}
                      {...register("blog_url", {
                        required: "Blog URL is required",
                      })}
                      placeholder="blog-slug-here"
                    />
                  </div>
                </InputField>
              </div>

              {/* SHORT TITLE - Required */}
              <div className="col-md-6">
                <InputField
                  icon={Type}
                  label="Short Title"
                  error={errors.short_title}
                  required
                >
                  <input
                    id="short_title"
                    className={`form-control ${errors.short_title ? "is-invalid" : ""}`}
                    {...register("short_title", {
                      required: "Short title is required",
                    })}
                    placeholder="Enter short title"
                  />
                </InputField>
              </div>

              {/* TAG - Required */}
              <div className="col-md-6">
                <InputField icon={Tag} label="Tag" error={errors.tag} required>
                  <input
                    id="tag"
                    className={`form-control ${errors.tag ? "is-invalid" : ""}`}
                    {...register("tag", {
                      required: "Tag is required",
                    })}
                    placeholder="e.g., Technology, Lifestyle"
                  />
                </InputField>
              </div>

              {/* CONTENT */}
              <div className="col-12">
                <SectionHeader
                  icon={Edit3}
                  title="Content"
                  subtitle="All fields are required"
                />
              </div>

              <div className="col-md-12">
                <InputField
                  icon={Type}
                  label="Hero Banner Text"
                  error={errors.long_title}
                  required
                >
                  <div className="border rounded">
                    <JoditEditorComponent
                      defaultValue={watch("long_title")}
                      onChange={(content) => setValue("long_title", content)}
                      height={120}
                      required={true}
                    />
                  </div>
                  {!longTitleContent && (
                    <small className="text-warning d-block mt-2">
                      ⚠️ Hero banner text is required
                    </small>
                  )}
                </InputField>
              </div>

              {/* DETAILS - Required */}
              <div className="col-12">
                <InputField
                  label="Blog Details"
                  error={errors.details}
                  required
                >
                  <div className="border rounded">
                    <JoditEditorComponent
                      defaultValue={watch("details")}
                      onChange={(content) => setValue("details", content)}
                      height={250}
                      required={true}
                    />
                  </div>
                  {!detailsContent && (
                    <small className="text-warning d-block mt-2">
                      ⚠️ Blog details content is required
                    </small>
                  )}
                </InputField>
              </div>

              {/* MEDIA - MOVED HERE */}
              <div className="col-12">
                <SectionHeader
                  icon={ImageIcon}
                  title="Media"
                  subtitle="Upload blog image (Required)"
                />
              </div>

              <div className="col-md-6">
                <div className="border rounded p-3 bg-light h-100">
                  <label className="d-block mb-2 fw-semibold text-dark">
                    <ImageIcon size={16} className="me-2 text-primary" />
                    Upload Image *
                  </label>
                  <input
                    type="file"
                    id="image"
                    className={`form-control ${!previewImage && isAdd ? "border-warning" : ""}`}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setImageFile(file);
                      setPreviewImage(URL.createObjectURL(file));
                    }}
                    accept="image/*"
                  />
                  <small className="text-muted d-block mt-1">
                    Recommended: 1200x630px, JPG/PNG, max 2MB
                  </small>
                  {!previewImage && isAdd && (
                    <small className="text-warning d-block mt-1">
                      ⚠️ Image is required
                    </small>
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="border rounded p-3 bg-light h-100">
                  <label className="d-block mb-2 fw-semibold text-dark">
                    Preview
                  </label>
                  {previewImage ? (
                    <div className="text-center">
                      <img
                        src={previewImage}
                        alt={watch("alt") || "Preview"}
                        className="img-fluid rounded border"
                        style={{ maxHeight: "150px" }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger mt-2"
                        onClick={() => {
                          setImageFile(null);
                          setPreviewImage("");
                          setValue("image", null);
                        }}
                        disabled={isView}
                      >
                        <XCircle size={14} className="me-1" />
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="text-center text-muted py-4">
                      <ImageIcon size={48} className="mb-2 opacity-50" />
                      <p className="mb-0">No image selected</p>
                      <small className="text-warning">Required field</small>
                    </div>
                  )}
                </div>
              </div>

              {/* IMAGE ALT FIELD - MOVED TO MEDIA SECTION */}
              <div className="col-md-12">
                <InputField
                  icon={FilePlus}
                  label="Image Alt Text"
                  error={errors.alt}
                  required
                >
                  <input
                    id="alt"
                    className={`form-control ${errors.alt ? "is-invalid" : ""}`}
                    {...register("alt", {
                      required: "Alt text is required",
                    })}
                    placeholder="Enter image alt text for accessibility"
                  />
                </InputField>
              </div>

              {/* SOCIAL MEDIA LINKS */}
              <div className="col-12">
                <SectionHeader
                  icon={Share2}
                  title="Social Media Links"
                  subtitle="Optional links for common platforms"
                />
              </div>

              {/* FACEBOOK */}
              <div className="col-md-6">
                <InputField
                  icon={Facebook}
                  label="Facebook Link"
                  error={errors.facebook}
                >
                  <input
                    id="facebook"
                    className="form-control"
                    {...register("facebook")}
                    placeholder="https://facebook.com/..."
                  />
                </InputField>
              </div>

              {/* INSTAGRAM */}
              <div className="col-md-6">
                <InputField
                  icon={Instagram}
                  label="Instagram Link"
                  error={errors.instagram}
                >
                  <input
                    id="instagram"
                    className="form-control"
                    {...register("instagram")}
                    placeholder="https://instagram.com/..."
                  />
                </InputField>
              </div>

              {/* TWITTER */}
              <div className="col-md-6">
                <InputField
                  icon={Twitter}
                  label="Twitter / X Link"
                  error={errors.twitter}
                >
                  <input
                    id="twitter"
                    className="form-control"
                    {...register("twitter")}
                    placeholder="https://twitter.com/..."
                  />
                </InputField>
              </div>

              {/* LINKEDIN */}
              <div className="col-md-6">
                <InputField
                  icon={Linkedin}
                  label="LinkedIn Link"
                  error={errors.linkedin}
                >
                  <input
                    id="linkedin"
                    className="form-control"
                    {...register("linkedin")}
                    placeholder="https://linkedin.com/..."
                  />
                </InputField>
              </div>

              {/* SETTINGS */}
              <div className="col-12">
                <SectionHeader icon={Grid} title="Settings" />
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="is_show_on_home_page"
                        className="form-check-input"
                        {...register("is_show_on_home_page")}
                        disabled={isView}
                        role="switch"
                      />
                      <label
                        htmlFor="is_show_on_home_page"
                        className="form-check-label d-flex align-items-center gap-2"
                      >
                        <Eye size={16} className="text-primary" />
                        Show on Homepage
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="is_active"
                        className="form-check-input"
                        {...register("is_active")}
                        disabled={isView}
                        defaultChecked
                        role="switch"
                      />
                      <label
                        htmlFor="is_active"
                        className="form-check-label d-flex align-items-center gap-2"
                      >
                        <CheckCircle size={16} className="text-success" />
                        Active
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>

          {/* FORM VALIDATION SUMMARY */}
          {(isAdd || isEdit) && Object.keys(errors).length > 0 && (
            <div className="alert alert-warning mt-3 mb-0">
              <AlertCircle size={16} className="me-2" />
              Please fill all required fields
            </div>
          )}

          {/* FOOTER */}
          <div className="d-flex justify-content-end gap-2 footer-modal mt-4 pt-3 border-top">
            <Button
              variant="outline-secondary"
              onClick={onHide}
              className="px-4"
            >
              {isView ? "Close" : "Cancel"}
            </Button>
            {!isView && (
              <Button
                type="submit"
                variant="primary"
                className="px-4 shadow-sm"
                disabled={Object.keys(errors).length > 0}
              >
                {isEdit ? "Save Changes" : "Create Blog"}
              </Button>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
